import { ref, readonly } from "vue";
import type { Ref } from "vue";

export const OWNER_LOCK_NAME = "gaia-sip-owner";

// Nome dedicato del popup SIP. La main app usa window.open(url, POPUP_WINDOW_NAME)
// per aprirlo o riottenere il riferimento dopo un proprio F5 (il browser referenzia
// le finestre per name finché restano aperte).
export const POPUP_WINDOW_NAME = "gaia-sip-popup";

// Tempo di tolleranza prima di dichiarare un duplicato. All'apertura il lock può
// risultare occupato per un istante da un detentore TRANSITORIO (vecchio documento
// in unload durante un reload del popup, o remount per :key): quello rilascia entro
// pochi ms. Accodiamo la richiesta e attendiamo fino a questa grace: se acquisiamo,
// siamo l'owner; se scade senza acquisire, il lock è tenuto da un owner reale e
// persistente → siamo un vero duplicato.
const DEFAULT_GRACE_MS = 2_000;

export interface AcquireOwnerLockOptions {
  /** Override della grace (ms) prima di dichiarare duplicato. Per i test. */
  graceMs?: number;
}

export interface SipOwnerLock {
  isOwner: Readonly<Ref<boolean>>;
  onDuplicate: (cb: () => void) => void;
  release: () => void;
}

/**
 * Acquisisce il Web Lock esclusivo dell'owner SIP **accodandosi** (non ifAvailable).
 * Se il lock è libero o si libera entro la grace, questa istanza diventa owner.
 * Se resta occupato oltre la grace, invoca onDuplicate (il chiamante si auto-chiude).
 *
 * L'accodamento è ciò che rende il popup robusto ai transitori di apertura
 * (reload via window.open(url, name) sulla stessa gesture, remount per :key):
 * il vecchio detentore rilascia in pochi ms e noi acquisiamo, invece di chiudere
 * la finestra al primo "occupato". Senza Web Locks: fallback owner (singola finestra).
 */
export function acquireOwnerLock(options: AcquireOwnerLockOptions = {}): SipOwnerLock {
  const graceMs = options.graceMs ?? DEFAULT_GRACE_MS;
  const isOwner = ref(false);
  const duplicateCbs: Array<() => void> = [];
  // Oggetto mutato in-place dalla callback async di navigator.locks.request:
  // catturare il primitivo nel return darebbe sempre il no-op iniziale.
  const releaseHolder = { fn: (): void => {} };
  const abort = new AbortController();
  let acquired = false;
  let duplicateFired = false;

  function onDuplicate(cb: () => void): void {
    duplicateCbs.push(cb);
  }

  function fireDuplicate(): void {
    if (acquired || duplicateFired) return;
    duplicateFired = true;
    // setTimeout(0): onDuplicate viene registrato subito dopo la chiamata sincrona;
    // garantiamo che la notifica lo raggiunga comunque.
    setTimeout(() => duplicateCbs.forEach((cb) => cb()), 0);
  }

  function release(): void {
    abort.abort(); // annulla una richiesta ancora in coda (se non ancora acquisita)
    releaseHolder.fn(); // rilascia il lock tenuto (se acquisito)
  }

  if (!("locks" in navigator)) {
    isOwner.value = true;
    return { isOwner: readonly(isOwner), onDuplicate, release };
  }

  // Se non acquisiamo entro la grace, il lock è di un owner reale → duplicato.
  const graceTimer = setTimeout(() => {
    if (acquired) return;
    abort.abort(); // smetti di stare in coda
    fireDuplicate();
  }, graceMs);

  navigator.locks
    .request(OWNER_LOCK_NAME, { mode: "exclusive", signal: abort.signal }, () => {
      acquired = true;
      clearTimeout(graceTimer);
      isOwner.value = true;
      return new Promise<void>((resolve) => {
        releaseHolder.fn = () => {
          isOwner.value = false;
          resolve();
        };
      });
    })
    .catch((err: unknown) => {
      clearTimeout(graceTimer);
      // AbortError = richiesta annullata da noi (grace scaduta o release): non è un errore.
      if (err instanceof DOMException && err.name === "AbortError") return;
      console.error("[SIP owner lock]", err);
    });

  return { isOwner: readonly(isOwner), onDuplicate, release };
}
